const INACTIVE_DOCUMENT_STATUSES = new Set(['superseded', 'archived', 'deleted', 'rejected', 'invalidated']);
const CV_REQUIREMENT_IDS = new Set(['cv', 'resume', 'curriculum_vitae']);

export const canonicalDocumentIdentity = (document) => {
  const ids = [document?.id, document?.file_id, document?.document_id]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  const uniqueIds = Array.from(new Set(ids));
  return uniqueIds.length === 1 ? uniqueIds[0] : null;
};

export const isActiveDocumentCandidate = (document) => {
  const status = String(document?.status || '').toLowerCase();
  const reviewStatus = String(document?.review_status || '').toLowerCase();
  return Boolean(
    document &&
    !INACTIVE_DOCUMENT_STATUSES.has(status) &&
    !INACTIVE_DOCUMENT_STATUSES.has(reviewStatus) &&
    reviewStatus !== 'amendment_requested' &&
    !document?.superseded_at &&
    !document?.deleted_at &&
    document?.is_active !== false,
  );
};

export const isPdfLikeDocument = (document) => {
  const fileName = document?.original_filename || document?.file_name || document?.file_url || '';
  const mimeType = String(document?.mime_type || document?.content_type || document?.file_type || '').toLowerCase();
  return mimeType === 'application/pdf' || /\.pdf(?:$|\?)/i.test(fileName);
};

// Multi-format CV parity (PDF/DOC/DOCX). The stored file is what the admin
// downloads/views/reviews, so classify from stored-file indicators first and
// only then fall back to the original (as-uploaded) filename.
export const getDocumentFileFormat = (document) => {
  if (!document) return null;
  const explicit = String(document?.file_format || '').trim().toLowerCase();
  if (['pdf', 'docx', 'doc'].includes(explicit)) return explicit;
  const mime = String(document?.mime_type || document?.content_type || document?.file_type || '').toLowerCase();
  const storedName = String(document?.file_name || document?.stored_filename || document?.file_url || '').toLowerCase();
  if (mime === 'application/pdf' || /\.pdf(?:$|\?)/i.test(storedName)) return 'pdf';
  if (mime.includes('openxmlformats-officedocument.wordprocessingml') || storedName.endsWith('.docx')) return 'docx';
  if (mime === 'application/msword' || storedName.endsWith('.doc')) return 'doc';
  const original = String(document?.original_filename || '').toLowerCase();
  if (original.endsWith('.pdf')) return 'pdf';
  if (original.endsWith('.docx')) return 'docx';
  if (original.endsWith('.doc')) return 'doc';
  return null;
};

export const documentHasPdfPreview = (document) =>
  Boolean(String(document?.preview_file_url || '').trim() || document?.has_pdf_preview);

// Reviewable = supported PDF/DOC/DOCX file (or already has a PDF preview).
export const isReviewableCvDocument = (document) =>
  Boolean(documentHasPdfPreview(document) || ['pdf', 'docx', 'doc'].includes(getDocumentFileFormat(document)));

// Inline PDF preview is only possible for a PDF or a doc with a PDF preview.
export const isInlinePreviewableCvDocument = (document) =>
  Boolean(isPdfLikeDocument(document) || documentHasPdfPreview(document));

export const isCvLikeDocument = (document) => {
  const label = [
    document?.requirement_name,
    document?.document_type_name,
    document?.document_label,
    document?.original_filename,
    document?.file_name,
  ].filter(Boolean).join(' ').toLowerCase();
  const requirementId = String(document?.requirement_id || '').toLowerCase();
  const documentType = String(document?.document_type || document?.document_type_name || '').toLowerCase();
  const documentSubtype = String(document?.document_subtype || '').toLowerCase();
  return Boolean(
    CV_REQUIREMENT_IDS.has(requirementId) ||
    ['cv', 'resume'].includes(documentType) ||
    documentSubtype === 'cv' ||
    /\b(cv|resume|curriculum vitae)\b/.test(label),
  );
};

export const getCvReviewState = (employee, documents = []) => {
  const linkedDocumentId = String(employee?.cv_document_id || '').trim();
  const cvDocuments = (documents || []).filter((document) => isCvLikeDocument(document));
  const exactLinkedDocuments = linkedDocumentId
    ? cvDocuments.filter((document) => canonicalDocumentIdentity(document) === linkedDocumentId)
    : [];
  const exactLinkedDocument = exactLinkedDocuments.length === 1 ? exactLinkedDocuments[0] : null;
  const exactLinkedReviewDocument = exactLinkedDocument &&
    isActiveDocumentCandidate(exactLinkedDocument) &&
    isReviewableCvDocument(exactLinkedDocument)
      ? exactLinkedDocument
      : null;
  const activeReviewableCvCandidates = cvDocuments.filter(
    (document) => (
      canonicalDocumentIdentity(document) &&
      isActiveDocumentCandidate(document) &&
      isReviewableCvDocument(document)
    ),
  );
  const fallbackReviewableCvDocument =
    activeReviewableCvCandidates.find((document) => canonicalDocumentIdentity(document) !== linkedDocumentId) ||
    activeReviewableCvCandidates[0] ||
    null;
  const linkableCvDocument = exactLinkedReviewDocument || fallbackReviewableCvDocument;

  return {
    cvDocuments,
    activeReviewableCvCandidates,
    linkableCvDocument,
    // Backwards-compatible aliases:
    activePdfCvCandidates: activeReviewableCvCandidates,
    linkablePdfCvDocument: linkableCvDocument,
    exactLinkedDocument,
    exactLinkedReviewDocument,
    cvReviewReady: Boolean(linkedDocumentId && exactLinkedReviewDocument),
    cvLinkRecoveryAvailable: Boolean(!exactLinkedReviewDocument && fallbackReviewableCvDocument),
  };
};

export const verifyLinkedCvRefresh = ({ employeeId, postResponse, refreshedEmployee, refreshedDocuments, refreshedDocument }) => {
  const response = postResponse || {};
  if (response.success !== true) {
    throw new Error('CV link was not accepted');
  }
  if (!response.cv_document_id || response.employee?.id !== employeeId) {
    throw new Error('CV link response was incomplete');
  }
  if (response.employee?.cv_document_id !== response.cv_document_id) {
    throw new Error('CV link response did not include the persisted employee link');
  }
  if (refreshedEmployee?.id !== employeeId || refreshedEmployee?.cv_document_id !== response.cv_document_id) {
    throw new Error('Employee refresh did not confirm the CV link');
  }
  const documents = Array.isArray(refreshedDocuments) ? [...refreshedDocuments] : [];
  if (refreshedDocument) {
    const detailIdentity = canonicalDocumentIdentity(refreshedDocument);
    const alreadyListed = detailIdentity && documents.some(
      (document) => canonicalDocumentIdentity(document) === detailIdentity,
    );
    if (!alreadyListed) {
      documents.push(refreshedDocument);
    }
  }
  const state = getCvReviewState(refreshedEmployee, documents);
  if (!state.cvReviewReady || canonicalDocumentIdentity(state.exactLinkedReviewDocument) !== response.cv_document_id) {
    throw new Error('Document refresh did not confirm the linked CV document');
  }
  return state;
};

export const shouldOfferServerCvLinkRecovery = ({
  cvReviewReady,
  cvLinkRecoveryAvailable,
  cvHasCanonicalEvidence,
  cvReplacementPending,
  cvApproved,
}) => Boolean(
  !cvReviewReady &&
  !cvLinkRecoveryAvailable &&
  cvHasCanonicalEvidence &&
  !cvReplacementPending &&
  !cvApproved,
);

export const shouldAcceptEmployeeResponse = (requestId, currentRequestId) => requestId === currentRequestId;
